import React, { useState } from 'react';
import { useRecoilState } from 'recoil';
import { css, SerializedStyles } from '@emotion/react';

import { TransferCheckState, userPageCurrentPageTransferState } from '@pages/Transfer/Transfer/atom/transfer';
import { Select, Input, DateRange, Checkbox, Sub1Button } from '@components/atoms';
import { getYYYYMMDDFormat } from '@components/atoms/Inputs/DateRangeMenu';
import { icons } from '@assets/icons';
import { CustomDateRangeButton, DateRangeStatic } from '@components/atoms/Inputs/DateRangeMenu/style';
import {
  ConditionsSectionWrapper,
  DateConditionSection,
  FilterDateRangeStyle,
  FilterSearchButtonStyle,
  FilterSearchConditionStyle,
  FilterSearchKeywordStyle,
  FiltersWrapper,
  SearchKeyConditionSection,
} from './styles';

export interface StyledProps {
  styles?: SerializedStyles;
}

export interface FiltersProps<T = any> {
  searchConditions?: any;
  setSearchConditions: React.Dispatch<React.SetStateAction<T>>;
  hasDateType?: boolean;
  styles?: SerializedStyles;
  conditionType?: {
    searchType?: any[];
    dateType?: any[];
  };
  hasRangeType?: boolean;
  hasTransferCheck?: boolean;
  filterKey?: string;
}

export const DEFAULT_SEARCH_KEY = [
  { label: 'User ID', value: 'ID', selected: true },
  { label: 'Name', value: 'NAME' },
];

export const DEFAULT_DATE_TYPE = [{ label: 'Create Date', value: 'CREATED_DATE', selected: true }];

const Filters = ({
  styles = css``,
  searchConditions,
  setSearchConditions,
  hasDateType = false,
  conditionType,
  hasRangeType = true,
  hasTransferCheck = false,
  filterKey = '',
}: FiltersProps) => {
  const [searchKey, setSearchKey] = useState(searchConditions?.searchKey || 'ID');
  const [searchKeyword, setSearchKeyword] = useState(searchConditions?.searchKeyword || '');
  const [searchDateType, setSearchDateType] = useState('CREATED_DATE');
  const [importDateRange, setImportDateRange] = useState([
    {
      key: 'selection',
      startDate: searchConditions?.dateRange?.startDate ? new Date(searchConditions.dateRange.startDate) : null,
      endDate: searchConditions?.dateRange?.endDate ? new Date(searchConditions.dateRange.endDate) : null,
    },
  ]);
  const selectedStaticRange = searchConditions?.selectedStaticRange; // (data-selected)

  const [ischecked, setIschecked] = useRecoilState(TransferCheckState);
  const [pageConditionsValue, setPageConditionsValue] = useRecoilState(userPageCurrentPageTransferState);

  // 기간 선택 옵션
  const dateOption = [
    { num: 1, dataValue: 'onemonth' },
    { num: 3, dataValue: 'threemonth' },
    { num: 6, dataValue: 'sixmonth' },
    { num: 12, dataValue: 'twelvemonth' },
  ];

  const doSearch = () => {
    const { startDate, endDate } = importDateRange[0];
    setSearchConditions(() => ({
      searchKey,
      searchKeyword,
      searchDateType,
      selectedStaticRange,
      dateRange: {
        key: 'selection',
        startDate: startDate ? getYYYYMMDDFormat(startDate || new Date()) : '',
        endDate: endDate ? getYYYYMMDDFormat(endDate || new Date()) : '',
      },
    }));
  };

  const checkboxHandler = () => {
    setPageConditionsValue((prev) => ({
      ...prev,
      ...{
        exist: prev.exist ? false : true,
      },
    }));
    setIschecked({ ischecked: pageConditionsValue.exist ? 'true' : 'false' });
  };

  /**
   * datarange 자동 변경
   */
  const onDateFilter = (date: string, num: number, setDataRange: any, e: React.MouseEvent) => {
    let calday = new Date();
    let tmpDate = {
      startDate: calday,
      endDate: new Date(),
      key: 'selection',
    };

    if (date === 'all') {
      setDataRange([{ ...tmpDate, startDate: null }]);
    } else {
      if (date === 'day') {
        calday.setDate(calday.getDate() - num);
      } else if (date === 'month') {
        calday.setMonth(calday.getMonth() - num);
        calday.setDate(calday.getDate() + 1);
      } else if (date === 'year') {
        calday.setFullYear(calday.getFullYear() - num);
        calday.setMonth(calday.getMonth());
        calday.setDate(calday.getDate() + 1);
      }

      setDataRange([{ ...tmpDate, startDate: calday }]);
      setSearchConditions(() => ({
        searchKey,
        searchKeyword,
        searchDateType,
        selectedStaticRange: (e as any).target.dataset.value,
        dateRange: {
          key: 'selection',
          startDate: calday ? getYYYYMMDDFormat(calday || new Date()) : '',
          endDate: tmpDate.endDate ? getYYYYMMDDFormat(tmpDate.endDate || new Date()) : '',
        },
      }));
    }
  };

  // 검색 및 날짜 초기화
  const resetHandler = () => {
    setSearchKeyword('');
    setImportDateRange([
      {
        key: 'selection',
        startDate: null,
        endDate: null,
      },
    ]);

    setSearchConditions(() => ({
      searchKey,
      searchKeyword: '',
      searchDateType,
      selectedStaticRange: '',
      dateRange: {
        key: 'selection',
        startDate: '',
        endDate: '',
      },
    }));
  };

  return (
    <FiltersWrapper className={filterKey} styles={styles}>
      {filterKey === 'log' ? (
        <>
          <ConditionsSectionWrapper>
            <SearchKeyConditionSection>
              <Input
                type={'text'}
                placeholder={'ID'}
                value={searchKeyword}
                onChange={(e) => setSearchKeyword(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') doSearch();
                }}
                styles={FilterSearchKeywordStyle}
              />
              <img src={icons.searchBlack} alt="search" onClick={(e) => doSearch()} />
            </SearchKeyConditionSection>

            {/* 첫번째 */}
            <DateConditionSection>
              {hasDateType && (
                <Select
                  list={conditionType?.dateType || DEFAULT_DATE_TYPE}
                  onSelected={(e) => setSearchDateType(e.value)}
                  styles={FilterSearchConditionStyle}
                />
              )}
              {hasRangeType && (
                <DateRangeStatic className={'logdaterange'} data-selected={selectedStaticRange}>
                  {dateOption.map((value, i) => (
                    <CustomDateRangeButton
                      key={value.num}
                      data-value={value.dataValue}
                      onClick={(e) => {
                        onDateFilter('month', value.num, setImportDateRange, e);
                      }}
                    >
                      {value.num}M
                    </CustomDateRangeButton>
                  ))}

                  <CustomDateRangeButton
                    className={'customrange'}
                    data-value={'custom'}
                    onClick={(e) => onDateFilter('custom', 1, setImportDateRange, e)}
                  >
                    <DateRange
                      defaultValue={searchConditions}
                      dateRangeSet={importDateRange}
                      setDateRangeSet={setImportDateRange}
                      placeholder={'Custom(Select range)'}
                      styles={FilterDateRangeStyle}
                      isTabVisible={false}
                      border={false}
                      isReadOnly={true}
                      setSearchConditions={setSearchConditions}
                      filterKey={filterKey}
                      doSearch={doSearch}
                    />
                  </CustomDateRangeButton>
                </DateRangeStatic>
              )}
            </DateConditionSection>
          </ConditionsSectionWrapper>
          <Sub1Button className="reset" styles={FilterSearchButtonStyle} onClick={resetHandler}>
            Reset
          </Sub1Button>
        </>
      ) : (
        <>
          <ConditionsSectionWrapper>
            <SearchKeyConditionSection>
              <Select
                list={
                  conditionType?.searchType ||
                  DEFAULT_SEARCH_KEY.map((item) => ({
                    ...item,
                    selected: item.value === searchKey,
                  }))
                }
                onSelected={(e) => setSearchKey(e.value)}
                styles={FilterSearchConditionStyle}
              />
              <Input
                type={'text'}
                placeholder={'Search Keyword'}
                value={searchKeyword}
                onChange={(e) => setSearchKeyword(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') doSearch();
                }}
                styles={FilterSearchKeywordStyle}
              />
            </SearchKeyConditionSection>
            <DateConditionSection>
              {hasDateType && (
                <Select
                  list={conditionType?.dateType || DEFAULT_DATE_TYPE}
                  onSelected={(e) => setSearchDateType(e.value)}
                  styles={FilterSearchConditionStyle}
                />
              )}
              {hasRangeType && (
                <DateRange
                  defaultValue={searchConditions}
                  dateRangeSet={importDateRange}
                  setDateRangeSet={setImportDateRange}
                  placeholder={'Select range'}
                  styles={FilterDateRangeStyle}
                />
              )}
            </DateConditionSection>
          </ConditionsSectionWrapper>
          <Sub1Button styles={FilterSearchButtonStyle} onClick={doSearch}>
            Search
          </Sub1Button>
        </>
      )}

      {hasTransferCheck && (
        <li>
          <Checkbox
            name="forcedAspects"
            checked={ischecked.ischecked === 'true' ? true : false}
            onChange={checkboxHandler}
          >
            Transfer
          </Checkbox>
        </li>
      )}
    </FiltersWrapper>
  );
};

export default Filters;
